// src/pages/AdminAccounting.tsx — Admin muhasebe: dönem kâr/zarar
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  ChevronLeft, ChevronRight, Download, Printer, Lock, Unlock, Search, RotateCcw, Trash2, Plus,
  TrendingUp, TrendingDown, Equal, Star,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { apiService } from '../services/api';
import type { AccountingReport } from '../services/api';

const MONTHS = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
const fmt = (n: number) => '$' + (n || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const r2 = (n: number) => Math.round(n * 100) / 100;
const clampPct = (v: any) => Math.max(0, Math.min(100, parseFloat(v) || 0));

export default function AdminAccounting() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [report, setReport] = useState<AccountingReport | null>(null);
  const [loading, setLoading] = useState(true);

  const [globalRate, setGlobalRate] = useState(50);
  const [workerRate, setWorkerRate] = useState(10);
  const [overrides, setOverrides] = useState<Record<string, number>>({});
  // BringGo / autoorder: "sisteme kazandırdığı" manuel değer (input metni; '' = sistem hesabı)
  const [manualGross, setManualGross] = useState<Record<string, string>>({});
  const [manualGrossSaved, setManualGrossSaved] = useState<Record<string, string>>({}); // sunucudaki değer
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<'gross' | 'cut' | 'name'>('gross');

  const [newIncome, setNewIncome] = useState({ label: '', amount: '' });
  const [newExpense, setNewExpense] = useState({ label: '', amount: '' });

  // ---- ÖDEME SEPETİ ----
  // Bir kişinin birden fazla hesabı olabiliyor; admin ödemeyi tek seferde yapıyor.
  // Seçim ANAHTARI userId: aynı kişi hem "Kullanıcı Kazançları" hem "Satış Ortaklığı"
  // tablosunda görünebildiği için ikisinde de AYNI tik, aynı kişiyi seçer. Böylece
  // ortaklık payı ayrıca aranmaz (gözden kaçmaz) ve iki kez sayılmaz.
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const toggleSelected = (userId: string) =>
    setSelected(prev => {
      const n = new Set(prev);
      if (n.has(userId)) n.delete(userId); else n.add(userId);
      return n;
    });

  const closed = !!report?.isClosed;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiService.getAccountingPeriod(year, month);
      setReport(r);
      setGlobalRate(r.userShareRatePercent);
      setWorkerRate(r.workerRatePercent);
      const ov: Record<string, number> = {};
      r.users.forEach(u => { if (u.isOverride) ov[u.userId] = u.ratePercent; });
      setOverrides(ov);
      const mg: Record<string, string> = {};
      r.users.forEach(u => { if (u.grossManual) mg[u.userId] = String(u.gross); });
      setManualGross(mg);
      setManualGrossSaved(mg);
    } catch (e: any) { toast.error(e?.message || 'Dönem yüklenemedi'); }
    finally { setLoading(false); }
  }, [year, month]);
  useEffect(() => { load(); }, [load]);

  const changeMonth = (delta: number) => {
    let m = month + delta, y = year;
    if (m < 1) { m = 12; y--; } else if (m > 12) { m = 1; y++; }
    setMonth(m); setYear(y);
    setSelected(new Set());   // dönem değişince sepet sıfırlanır
  };

  // ---- canlı hesap (client) ----
  const c = useMemo(() => {
    if (!report) return null;
    let gross = 0, cut = 0;
    const rows = report.users.map(u => {
      const isOv = overrides[u.userId] !== undefined;
      const rate = isOv ? overrides[u.userId] : globalRate;
      // Manuel brüt (yalnız BringGo / autoorder): girilen rakam varsa hesap onun üzerinden
      const mgRaw = u.grossEditable ? (manualGross[u.userId] ?? '') : '';
      const isGrossManual = mgRaw.trim() !== '';
      const g = isGrossManual ? r2(Math.max(0, parseFloat(mgRaw.replace(',', '.')) || 0))
        : (u.grossEditable ? r2(u.computedGross ?? 0) : u.gross);
      const cutV = r2(g * rate / 100);
      gross += g; cut += cutV;
      return { ...u, gross: g, isGrossManual, rate, isOv, cut: cutV, keep: r2(g - cutV) };
    });
    gross = r2(gross); cut = r2(cut);
    const affiliate = r2(report.affiliatePayout || 0);
    const incomeTotal = r2(report.income.reduce((a, b) => a + b.amount, 0));
    const expenseTotal = r2(cut + affiliate + report.expenseManual.reduce((a, b) => a + b.amount, 0));
    const net = r2(incomeTotal - expenseTotal);
    const worker = r2(Math.max(0, net) * workerRate / 100);
    const final = r2(net - worker);
    return { rows, gross, cut, affiliate, keep: r2(gross - cut), incomeTotal, expenseTotal, net, worker, final };
  }, [report, globalRate, workerRate, overrides, manualGross]);

  const visibleRows = useMemo(() => {
    if (!c) return [];
    const q = search.trim().toLocaleLowerCase('tr');
    let list = c.rows.slice();
    if (q) list = list.filter(u => u.name.toLocaleLowerCase('tr').includes(q) || u.email.toLocaleLowerCase('tr').includes(q));
    if (sort === 'gross') list.sort((a, b) => b.gross - a.gross);
    else if (sort === 'cut') list.sort((a, b) => b.cut - a.cut);
    else list.sort((a, b) => a.name.localeCompare(b.name, 'tr'));
    return list;
  }, [c, search, sort]);

  // Kişi bazında hızlı erişim: aynı userId hem hesap payı hem ortaklık komisyonu taşıyabilir.
  const affByUser = useMemo(() => {
    const m = new Map<string, { name: string; email: string; referralCode: string; commission: number }>();
    (report?.affiliates ?? []).forEach(a => m.set(a.userId, a));
    return m;
  }, [report]);

  const cutByUser = useMemo(() => {
    const m = new Map<string, { name: string; email: string; cut: number }>();
    (c?.rows ?? []).forEach(u => m.set(u.userId, { name: u.name, email: u.email, cut: u.cut }));
    return m;
  }, [c]);

  // Seçilenlerin toplamı — hesap payı + satış ortaklığı TEK toplamda
  const basket = useMemo(() => {
    let accountTotal = 0, affiliateTotal = 0;
    const lines: { userId: string; name: string; email: string; cut: number; commission: number }[] = [];
    selected.forEach(id => {
      const u = cutByUser.get(id);
      const a = affByUser.get(id);
      if (!u && !a) return;                       // dönem değişmiş olabilir, artık yoksa atla
      const cut = u?.cut ?? 0;
      const commission = a?.commission ?? 0;
      accountTotal += cut; affiliateTotal += commission;
      lines.push({
        userId: id,
        name: u?.name ?? a?.name ?? '(bilinmeyen)',
        email: u?.email ?? a?.email ?? '',
        cut, commission,
      });
    });
    lines.sort((x, y) => (y.cut + y.commission) - (x.cut + x.commission));
    return {
      lines,
      accountTotal: r2(accountTotal),
      affiliateTotal: r2(affiliateTotal),
      total: r2(accountTotal + affiliateTotal),
    };
  }, [selected, cutByUser, affByUser]);

  const copyBasket = async () => {
    const head = `${MONTHS[month - 1]} ${year} — ödeme özeti (kazanç dönemi: ${MONTHS[(report?.earningMonth ?? month) - 1]} ${report?.earningYear ?? year})`;
    const body = basket.lines.map(l => {
      const parts = [`  ${l.name} (${l.email})`];
      if (l.cut > 0) parts.push(`    hesap payı      ${fmt(l.cut)}`);
      if (l.commission > 0) parts.push(`    satış ortaklığı ${fmt(l.commission)}`);
      return parts.join('\n');
    }).join('\n');
    const foot = [
      '',
      `Hesap payları toplamı : ${fmt(basket.accountTotal)}`,
      `Satış ortaklığı       : ${fmt(basket.affiliateTotal)}`,
      `GENEL TOPLAM          : ${fmt(basket.total)}`,
    ].join('\n');
    try {
      await navigator.clipboard.writeText([head, '', body, foot].join('\n'));
      toast.success('Ödeme özeti kopyalandı');
    } catch { toast.error('Kopyalanamadı'); }
  };

  // ---- kalıcılık ----
  const saveRates = (body: { userShareRatePercent?: number; workerRatePercent?: number }) =>
    apiService.setAccountingRates(year, month, body).catch(() => toast.error('Oran kaydedilemedi'));
  const saveUserRate = (userId: string, rate: number | null) =>
    apiService.setAccountingUserRate(year, month, userId, rate).catch(() => toast.error('Kaydedilemedi'));

  const setUserOverride = (userId: string, val: string) => {
    setOverrides(prev => ({ ...prev, [userId]: clampPct(val) }));
  };
  // Manuel brüt kaydet: boş → sistem hesabına dön. Sunucu devir listelerini de buna göre
  // yeniden hesapladığı için kayıttan sonra dönem tazelenir.
  const saveManualGross = async (userId: string, raw: string | null) => {
    const t = (raw ?? '').trim();
    if (t === (manualGrossSaved[userId] ?? '').trim()) return; // değişmedi
    const val = t === '' ? null : Math.max(0, parseFloat(t.replace(',', '.')) || 0);
    try { await apiService.setAccountingUserGross(year, month, userId, val); load(); }
    catch (e: any) { toast.error(e?.message || 'Kaydedilemedi'); }
  };
  const resetManualGross = (userId: string) => {
    setManualGross(prev => { const n = { ...prev }; delete n[userId]; return n; });
    saveManualGross(userId, null);
  };

  const resetUserOverride = (userId: string) => {
    setOverrides(prev => { const n = { ...prev }; delete n[userId]; return n; });
    saveUserRate(userId, null);
  };

  const addIncome = async () => {
    if (!newIncome.label.trim()) return;
    try { await apiService.addAccountingIncome(year, month, newIncome.label.trim(), parseFloat(newIncome.amount) || 0);
      setNewIncome({ label: '', amount: '' }); load(); } catch { toast.error('Eklenemedi'); }
  };
  const addExpense = async () => {
    if (!newExpense.label.trim()) return;
    try { await apiService.addAccountingExpense(year, month, newExpense.label.trim(), parseFloat(newExpense.amount) || 0);
      setNewExpense({ label: '', amount: '' }); load(); } catch { toast.error('Eklenemedi'); }
  };
  const delIncome = async (id: string) => { try { await apiService.deleteAccountingIncome(id); load(); } catch { toast.error('Silinemedi'); } };
  const delExpense = async (id: string) => { try { await apiService.deleteAccountingExpense(id); load(); } catch { toast.error('Silinemedi'); } };

  const toggleClose = async () => {
    try {
      if (closed) { if (!window.confirm('Dönemi yeniden açmak istiyor musun? Snapshot silinecek, rakamlar tekrar canlıya döner.')) return;
        const r = await apiService.reopenAccountingPeriod(year, month); setReport(r); toast.success('Dönem açıldı'); }
      else { if (!window.confirm('Dönemi kapatmak rakamları dondurur (snapshot). Emin misin?')) return;
        const r = await apiService.closeAccountingPeriod(year, month); setReport(r); toast.success('Dönem kapatıldı'); }
    } catch (e: any) { toast.error(e?.message || 'İşlem başarısız'); }
  };

  const exportCsv = () => {
    if (!c) return;
    const esc = (v: any) => { const s = String(v ?? ''); return /[",;\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
    const L: string[] = [];
    L.push(`EarnBackHub — Dönem Muhasebe Raporu`);
    L.push(`Ödeme ayı:,${MONTHS[month - 1]} ${year},Sipariş dönemi:,${MONTHS[report!.earningMonth - 1]} ${report!.earningYear},Durum:,${closed ? 'Kapalı' : 'Açık'}`);
    L.push('');
    L.push('KULLANICI KAZANÇLARI');
    L.push(['Kullanıcı', 'E-posta', 'Brüt Komisyon', 'Pay Oranı', 'Kullanıcı Payı', 'Sisteme Kalan'].map(esc).join(','));
    c.rows.forEach(u => L.push([u.name, u.email, u.gross.toFixed(2), '%' + u.rate, u.cut.toFixed(2), u.keep.toFixed(2)].map(esc).join(',')));
    L.push(['TOPLAM', '', c.gross.toFixed(2), '', c.cut.toFixed(2), c.keep.toFixed(2)].map(esc).join(','));
    L.push(''); L.push('GELİRLER'); L.push('Kalem,Tutar');
    report!.income.forEach(i => L.push([i.label, i.amount.toFixed(2)].map(esc).join(',')));
    L.push(['Toplam Gelir', c.incomeTotal.toFixed(2)].map(esc).join(','));
    L.push(''); L.push('GİDERLER'); L.push('Kalem,Tutar');
    L.push(['Kullanıcı kazançları / cashback (otomatik)', c.cut.toFixed(2)].map(esc).join(','));
    L.push(['Satış Ortaklığı komisyonu (otomatik)', c.affiliate.toFixed(2)].map(esc).join(','));
    report!.expenseManual.forEach(i => L.push([i.label, i.amount.toFixed(2)].map(esc).join(',')));
    L.push(['Toplam Gider', c.expenseTotal.toFixed(2)].map(esc).join(','));
    L.push(''); L.push('ÖZET');
    L.push(['Net Kâr', c.net.toFixed(2)].map(esc).join(','));
    L.push([`İşçi Payı (%${workerRate})`, c.worker.toFixed(2)].map(esc).join(','));
    L.push(['Nihai Net', c.final.toFixed(2)].map(esc).join(','));
    const blob = new Blob(['﻿' + L.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
    a.download = `muhasebe-${year}-${String(month).padStart(2, '0')}.csv`; a.click(); URL.revokeObjectURL(a.href);
  };

  const printReport = () => {
    if (!c) return;
    const rowsHtml = c.rows.map(u => `<tr><td>${u.name}<div class="m">${u.email}</div></td><td class="n">${fmt(u.gross)}</td><td class="n">%${u.rate}</td><td class="n cut">${fmt(u.cut)}</td><td class="n keep">${fmt(u.keep)}</td></tr>`).join('');
    const inc = report!.income.map(i => `<div class="kv"><span>${i.label}</span><span>${fmt(i.amount)}</span></div>`).join('');
    const exp = `<div class="kv"><span>Kullanıcı kazançları / cashback (oto.)</span><span>${fmt(c.cut)}</span></div>`
      + `<div class="kv"><span>Satış Ortaklığı komisyonu (oto.)</span><span>${fmt(c.affiliate)}</span></div>`
      + report!.expenseManual.map(i => `<div class="kv"><span>${i.label}</span><span>${fmt(i.amount)}</span></div>`).join('');
    const w = window.open('', '_blank'); if (!w) return;
    w.document.write(`<html><head><title>Muhasebe ${MONTHS[month-1]} ${year}</title><meta charset="utf-8"><style>
      body{font-family:-apple-system,Segoe UI,Arial,sans-serif;color:#111827;max-width:800px;margin:0 auto;padding:32px}
      .head{display:flex;justify-content:space-between;border-bottom:2px solid #111827;padding-bottom:12px}
      .co{font-size:20px;font-weight:800}.co span{color:#2563eb}.meta{text-align:right;font-size:12px;color:#4b5563;line-height:1.7}
      h3{font-size:11px;text-transform:uppercase;letter-spacing:.06em;color:#6b7280;margin:24px 0 8px}
      table{width:100%;border-collapse:collapse;font-size:12px}th,td{padding:6px 8px;text-align:right;border-bottom:1px solid #eef0f3}
      th:first-child,td:first-child{text-align:left}th{font-size:9px;text-transform:uppercase;color:#6b7280;border-bottom:1.5px solid #d1d5db}
      .m{color:#9ca3af;font-size:10px}.n{font-variant-numeric:tabular-nums}.cut{color:#dc2626}.keep{color:#16a34a}
      tfoot td{font-weight:800;border-top:2px solid #111827}.two{display:grid;grid-template-columns:1fr 1fr;gap:28px;margin-top:20px}
      .kv{display:flex;justify-content:space-between;font-size:12px;padding:5px 0;border-bottom:1px solid #eef0f3}
      .sum{margin-top:22px;border:1.5px solid #111827;border-radius:6px}.row{display:flex;justify-content:space-between;padding:9px 14px;font-size:12px;border-bottom:1px solid #eef0f3}
      .row.net{background:#f9fafb;font-weight:800}.row.fin{background:#fffbeb;color:#b45309;font-weight:800;font-size:14px}
    </style></head><body>
      <div class="head"><div><div class="co">Earn<span>Back</span>Hub</div><div style="font-size:12px;color:#4b5563">Dönem Muhasebe Raporu</div></div>
      <div class="meta"><b>Ödeme ayı:</b> ${MONTHS[month-1]} ${year}<br><b>Sipariş dönemi:</b> ${MONTHS[report!.earningMonth-1]} ${report!.earningYear}<br><b>Durum:</b> ${closed?'Kapalı':'Açık'} · USD</div></div>
      <h3>Kullanıcı Kazançları</h3>
      <table><thead><tr><th>Kullanıcı</th><th>Brüt</th><th>Oran</th><th>Kullanıcı Payı</th><th>Sisteme Kalan</th></tr></thead>
      <tbody>${rowsHtml}</tbody><tfoot><tr><td>TOPLAM (${c.rows.length})</td><td class="n">${fmt(c.gross)}</td><td></td><td class="n cut">${fmt(c.cut)}</td><td class="n keep">${fmt(c.keep)}</td></tr></tfoot></table>
      <div class="two"><div><h3 style="margin-top:0">Gelirler</h3>${inc}<div class="kv" style="font-weight:800;border-top:1.5px solid #111827"><span>Toplam Gelir</span><span>${fmt(c.incomeTotal)}</span></div></div>
      <div><h3 style="margin-top:0">Giderler</h3>${exp}<div class="kv" style="font-weight:800;border-top:1.5px solid #111827"><span>Toplam Gider</span><span>${fmt(c.expenseTotal)}</span></div></div></div>
      <div class="sum"><div class="row"><span>Toplam Gelir</span><span>${fmt(c.incomeTotal)}</span></div>
      <div class="row"><span>Toplam Gider</span><span>${fmt(c.expenseTotal)}</span></div>
      <div class="row net"><span>Net Kâr</span><span>${fmt(c.net)}</span></div>
      <div class="row"><span>İşçi Payı (%${workerRate})</span><span>${fmt(c.worker)}</span></div>
      <div class="row fin"><span>Nihai Net — Sana Kalan</span><span>${fmt(c.final)}</span></div></div>
    </body></html>`);
    w.document.close(); setTimeout(() => w.print(), 300);
  };

  const KPI = ({ label, value, sub, color, Icon }: any) => (
    <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
      <div className="flex items-center justify-between">
        <span className="text-xs text-gray-500 font-medium">{label}</span>
        <span className={`w-8 h-8 rounded-lg grid place-items-center ${color.bg}`}><Icon className={`w-4 h-4 ${color.text}`} /></span>
      </div>
      <div className={`mt-3 text-2xl font-bold tabular-nums ${color.val}`}>{value}</div>
      <div className="mt-0.5 text-xs text-gray-400">{sub}</div>
    </div>
  );

  return (
    <div className="space-y-5">
      {/* header */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center bg-white border border-gray-200 rounded-lg p-1 shadow-sm">
          <button onClick={() => changeMonth(-1)} className="w-8 h-8 rounded-md text-gray-500 hover:bg-gray-100 grid place-items-center"><ChevronLeft className="w-4 h-4" /></button>
          <span className="px-3 font-semibold text-sm min-w-[130px] text-center">{MONTHS[month - 1]} {year}</span>
          <button onClick={() => changeMonth(1)} className="w-8 h-8 rounded-md text-gray-500 hover:bg-gray-100 grid place-items-center"><ChevronRight className="w-4 h-4" /></button>
        </div>
        <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${closed ? 'bg-gray-100 text-gray-600' : 'bg-green-50 text-green-700'}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${closed ? 'bg-gray-500' : 'bg-green-500'}`} />{closed ? 'Kapalı' : 'Açık'}
        </span>
        {c && (
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700" title="Ödeme penceresi = sipariş dönemi + 3 ay">
            {MONTHS[report!.earningMonth - 1]} {report!.earningYear} dönemi ödemesi
          </span>
        )}
        <div className="ml-auto flex items-center gap-2">
          <button onClick={exportCsv} className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm text-gray-700 hover:bg-gray-50 shadow-sm"><Download className="w-4 h-4" />CSV</button>
          <button onClick={printReport} className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm text-gray-700 hover:bg-gray-50 shadow-sm"><Printer className="w-4 h-4" />Yazdır</button>
          <button onClick={toggleClose} className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-white ${closed ? 'bg-amber-600 hover:bg-amber-700' : 'bg-blue-600 hover:bg-blue-700'}`}>
            {closed ? <><Unlock className="w-4 h-4" />Yeniden Aç</> : <><Lock className="w-4 h-4" />Dönemi Kapat</>}
          </button>
        </div>
      </div>

      {loading || !c ? (
        <div className="py-20 text-center text-gray-400">Yükleniyor…</div>
      ) : (
        <>
          {/* KPI */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <KPI label="Toplam Gelir" value={fmt(c.incomeTotal)} sub={`${report!.income.length} kalem`} Icon={TrendingUp} color={{ bg: 'bg-green-50', text: 'text-green-600', val: 'text-green-600' }} />
            <KPI label="Toplam Gider" value={fmt(c.expenseTotal)} sub={`Kullanıcı payı + Satış Ortaklığı + ${report!.expenseManual.length} kalem`} Icon={TrendingDown} color={{ bg: 'bg-red-50', text: 'text-red-600', val: 'text-red-600' }} />
            <KPI label="Net Kâr" value={fmt(c.net)} sub="Gelir − Gider" Icon={Equal} color={{ bg: 'bg-blue-50', text: 'text-blue-600', val: 'text-gray-800' }} />
            <KPI label="Nihai Net" value={fmt(c.final)} sub="İşçi payı sonrası — sana kalan" Icon={Star} color={{ bg: 'bg-amber-50', text: 'text-amber-600', val: 'text-amber-600' }} />
          </div>

          {/* kullanıcı kazançları */}
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
            <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100 flex-wrap">
              <h2 className="text-base font-semibold text-gray-900">Kullanıcı Kazançları</h2>
              <span className="text-xs text-gray-500">Doğrulanmış siparişlerden brüt komisyon</span>
              <div className="ml-auto flex items-center gap-2 text-sm text-gray-500">Kullanıcı payı oranı
                <input type="number" min={0} max={100} value={globalRate} disabled={closed}
                  onChange={e => setGlobalRate(clampPct(e.target.value))}
                  onBlur={() => !closed && saveRates({ userShareRatePercent: globalRate })}
                  className="w-16 text-right tabular-nums px-2 py-1.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-100 focus:border-blue-500 disabled:bg-gray-50" /> %
              </div>
            </div>
            <div className="flex items-center gap-2 px-5 py-3 border-b border-gray-100 flex-wrap">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Kullanıcı ara…"
                  className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-100 focus:border-blue-500" />
              </div>
              <span className="text-xs text-gray-500 whitespace-nowrap">{visibleRows.length === c.rows.length ? `${c.rows.length} kullanıcı` : `${visibleRows.length} / ${c.rows.length}`}</span>
              <select value={sort} onChange={e => setSort(e.target.value as any)} className="text-sm px-3 py-2 border border-gray-200 rounded-lg">
                <option value="gross">En çok kazandıran</option>
                <option value="cut">En çok pay alan</option>
                <option value="name">İsme göre</option>
              </select>
            </div>
            <div className="overflow-auto max-h-[380px]">
              <table className="w-full text-sm">
                <thead className="sticky top-0 z-10">
                  <tr className="bg-gray-50 text-left text-[11px] uppercase tracking-wide text-gray-500">
                    <th className="pl-5 pr-2 py-3 w-9">
                      <input
                        type="checkbox"
                        title="Görünen tüm satırları seç"
                        className="w-4 h-4 accent-blue-600 cursor-pointer"
                        checked={visibleRows.length > 0 && visibleRows.every(u => selected.has(u.userId))}
                        onChange={e => {
                          const on = e.target.checked;
                          setSelected(prev => {
                            const n = new Set(prev);
                            visibleRows.forEach(u => { if (on) n.add(u.userId); else n.delete(u.userId); });
                            return n;
                          });
                        }} />
                    </th>
                    <th className="px-5 py-3 font-semibold">Kullanıcı</th>
                    <th className="px-5 py-3 font-semibold text-right">Sisteme kazandırdığı</th>
                    <th className="px-5 py-3 font-semibold text-center">Pay oranı</th>
                    <th className="px-5 py-3 font-semibold text-right">Kullanıcı payı</th>
                    <th className="px-5 py-3 font-semibold text-right">Sisteme kalan</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.length === 0 ? (
                    <tr><td colSpan={6} className="px-5 py-10 text-center text-gray-400">Kullanıcı yok</td></tr>
                  ) : visibleRows.map(u => {
                    // Bu kullanıcının tutarının ne kadarı önceki dönemlerden devredip bu ay ödeniyor
                    const uCarryIns = (report!.carryIn ?? []).filter(x => x.userId === u.userId);
                    const uAff = affByUser.get(u.userId);      // aynı kişinin ortaklık komisyonu
                    const isSel = selected.has(u.userId);
                    return (
                    <tr key={u.userId} className={`border-b border-gray-50 ${isSel ? 'bg-blue-50/70' : 'hover:bg-gray-50'}`}>
                      <td className="pl-5 pr-2 py-3 align-top">
                        <input type="checkbox" className="w-4 h-4 accent-blue-600 cursor-pointer mt-1"
                          checked={isSel} onChange={() => toggleSelected(u.userId)} />
                      </td>
                      <td className="px-5 py-3">
                        <div className="font-semibold text-gray-900">{u.name}</div>
                        <div className="text-xs text-gray-400">{u.email}</div>
                        {uAff && uAff.commission > 0 && (
                          <div className="mt-1 inline-flex items-center gap-1 text-[11px] text-purple-700 bg-purple-50 border border-purple-200 rounded px-1.5 py-0.5">
                            + satış ortaklığı {fmt(uAff.commission)} — seçince toplama dahil olur
                          </div>
                        )}
                        {uCarryIns.map((x, i) => (
                          <div key={`ci-${i}`} className="mt-1 inline-flex items-center gap-1 text-[11px] text-sky-700 bg-sky-50 border border-sky-200 rounded px-1.5 py-0.5">
                            ↪ {fmt(x.gross)} — {x.fromPeriodName} döneminden devredildi
                          </div>
                        ))}
                      </td>
                      <td className="px-5 py-3 text-right tabular-nums">
                        {u.grossEditable ? (
                          <div className="flex flex-col items-end gap-1">
                            <div className="flex items-center gap-1.5">
                              {u.isGrossManual && !closed && (
                                <button onClick={() => resetManualGross(u.userId)} title="Sistem hesabına dön" className="text-gray-400 hover:text-orange-600">
                                  <RotateCcw className="w-3.5 h-3.5" />
                                </button>
                              )}
                              <span className="text-gray-400 text-sm">$</span>
                              <input type="number" min={0} step="0.01" disabled={closed}
                                value={manualGross[u.userId] ?? ''}
                                placeholder={(u.computedGross ?? 0).toFixed(2)}
                                onChange={e => setManualGross(prev => ({ ...prev, [u.userId]: e.target.value }))}
                                onBlur={e => !closed && saveManualGross(u.userId, e.target.value)}
                                onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                                className={`w-28 text-right tabular-nums text-sm px-2 py-1 border rounded-md focus:ring-2 focus:ring-orange-100 focus:border-orange-500 disabled:bg-gray-50 ${u.isGrossManual ? 'border-orange-400 bg-orange-50 text-orange-700 font-semibold' : 'border-gray-200'}`} />
                            </div>
                            <div className="text-[11px] text-gray-400 font-normal">
                              {u.isGrossManual ? <><span className="text-orange-600 font-semibold">manuel</span> · sistem {fmt(u.computedGross ?? 0)}</> : 'sistem hesabı · rakam girilebilir'}
                            </div>
                          </div>
                        ) : fmt(u.gross)}
                        {!u.grossEditable && uCarryIns.length > 0 && (
                          <div className="text-[11px] text-gray-400 font-normal">
                            bu dönem {fmt(u.gross - uCarryIns.reduce((a, b) => a + b.gross, 0))}
                            {' + '}devir {fmt(uCarryIns.reduce((a, b) => a + b.gross, 0))}
                          </div>
                        )}
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex items-center justify-center gap-1.5">
                          <input type="number" min={0} max={100} value={u.rate} disabled={closed}
                            onChange={e => setUserOverride(u.userId, e.target.value)}
                            onBlur={() => !closed && saveUserRate(u.userId, overrides[u.userId] ?? null)}
                            className={`w-14 text-right tabular-nums text-sm px-1.5 py-1 border rounded-md focus:ring-2 focus:ring-blue-100 focus:border-blue-500 disabled:bg-gray-50 ${u.isOv ? 'border-blue-400 bg-blue-50 text-blue-700 font-semibold' : 'border-gray-200'}`} />
                          <span className="text-gray-400 text-sm">%</span>
                          {u.isOv && !closed && <button onClick={() => resetUserOverride(u.userId)} title="Global orana dön" className="text-gray-400 hover:text-blue-600"><RotateCcw className="w-3.5 h-3.5" /></button>}
                        </div>
                      </td>
                      <td className="px-5 py-3 text-right tabular-nums text-red-600">{fmt(u.cut)}</td>
                      <td className="px-5 py-3 text-right tabular-nums text-green-600">{fmt(u.keep)}</td>
                    </tr>
                    );
                  })}
                </tbody>
                <tfoot className="sticky bottom-0 z-10">
                  <tr className="bg-gray-50 border-t-2 border-gray-200 font-bold text-sm">
                    <td></td>
                    <td className="px-5 py-3.5 uppercase text-[11px] tracking-wide text-gray-500">Toplam</td>
                    <td className="px-5 py-3.5 text-right tabular-nums">{fmt(c.gross)}</td>
                    <td></td>
                    <td className="px-5 py-3.5 text-right tabular-nums text-red-600">{fmt(c.cut)}</td>
                    <td className="px-5 py-3.5 text-right tabular-nums text-green-600">{fmt(c.keep)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* satış ortaklığı — referrer bazlı */}
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
            <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100 flex-wrap">
              <h2 className="text-base font-semibold text-gray-900">Satış Ortaklığı</h2>
              <span className="text-xs text-gray-500">Getirdiği kullanıcıların doğrulanmış kazancının %20'si</span>
              <span className="ml-auto text-xs text-gray-500">{report!.affiliates.length} ortak</span>
            </div>
            {report!.affiliates.length === 0 ? (
              <div className="px-5 py-8 text-center text-gray-400 text-sm">Bu dönemde satış ortaklığı komisyonu yok</div>
            ) : (
              <div className="overflow-auto max-h-[380px]">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 z-10">
                    <tr className="bg-gray-50 text-left text-[11px] uppercase tracking-wide text-gray-500">
                      <th className="pl-5 pr-2 py-3 w-9"></th>
                      <th className="px-5 py-3 font-semibold">Satış Ortağı</th>
                      <th className="px-5 py-3 font-semibold text-right">Getirdiği kazanç</th>
                      <th className="px-5 py-3 font-semibold text-center">Oran</th>
                      <th className="px-5 py-3 font-semibold text-right">Komisyon</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report!.affiliates.map(a => {
                      const isSel = selected.has(a.userId);
                      const hasAccount = cutByUser.has(a.userId);
                      return (
                      <tr key={a.userId} className={`border-b border-gray-50 ${isSel ? 'bg-blue-50/70' : 'hover:bg-gray-50'}`}>
                        <td className="pl-5 pr-2 py-3 align-top">
                          <input type="checkbox" className="w-4 h-4 accent-blue-600 cursor-pointer mt-1"
                            checked={isSel} onChange={() => toggleSelected(a.userId)} />
                        </td>
                        <td className="px-5 py-3">
                          <div className="font-semibold text-gray-900">{a.name}</div>
                          <div className="text-xs text-gray-400">{a.email}{a.referralCode ? ` · ${a.referralCode}` : ''}</div>
                          {hasAccount && (
                            <div className="mt-1 inline-flex items-center gap-1 text-[11px] text-gray-600 bg-gray-100 border border-gray-200 rounded px-1.5 py-0.5">
                              Bu kişinin hesap payı da var — tek tik ikisini birden alır
                            </div>
                          )}
                        </td>
                        <td className="px-5 py-3 text-right tabular-nums">{fmt(a.referredEarnings)}</td>
                        <td className="px-5 py-3 text-center text-gray-400">%20</td>
                        <td className="px-5 py-3 text-right tabular-nums text-red-600">
                          {fmt(a.commission)}
                          {a.rawCommission != null && a.rawCommission > a.commission + 0.005 && (
                            <div className="text-[11px] text-gray-400 font-normal" title="Getirdiği müşterilerden en az birinde ömür boyu 500 USD tavanı doldu">
                              ham {fmt(a.rawCommission)} · müşteri tavanı
                            </div>
                          )}
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                  <tfoot className="sticky bottom-0 z-10">
                    <tr className="bg-gray-50 border-t-2 border-gray-200 font-bold text-sm">
                      <td></td>
                      <td className="px-5 py-3.5 uppercase text-[11px] tracking-wide text-gray-500">Toplam</td>
                      <td className="px-5 py-3.5 text-right tabular-nums">{fmt(report!.affiliateReferredTotal)}</td>
                      <td></td>
                      <td className="px-5 py-3.5 text-right tabular-nums text-red-600">{fmt(c.affiliate)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>

          {/* gelir + gider */}
          <div className="grid md:grid-cols-2 gap-5">
            {/* gelir */}
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100"><h2 className="text-base font-semibold text-green-600">Gelirler</h2><span className="text-xs text-gray-500">Admin elle girer</span></div>
              {report!.income.map(i => (
                <div key={i.id} className="flex items-center gap-3 px-5 py-3 border-b border-gray-50 text-sm">
                  <span className="flex-1">{i.label}</span><span className="tabular-nums font-medium">{fmt(i.amount)}</span>
                  {!closed && <button onClick={() => delIncome(i.id)} className="text-gray-300 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>}
                </div>
              ))}
              {!closed && (
                <div className="flex items-center gap-2 px-4 py-3">
                  <input value={newIncome.label} onChange={e => setNewIncome(s => ({ ...s, label: e.target.value }))} placeholder="Gelir açıklaması" className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-100 focus:border-blue-500" />
                  <input value={newIncome.amount} onChange={e => setNewIncome(s => ({ ...s, amount: e.target.value }))} onKeyDown={e => e.key === 'Enter' && addIncome()} placeholder="0,00" className="w-28 text-right tabular-nums px-3 py-2 border border-gray-200 rounded-lg text-sm" />
                  <button onClick={addIncome} className="inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-dashed border-gray-300 text-sm text-gray-500 hover:border-blue-500 hover:text-blue-600"><Plus className="w-4 h-4" /></button>
                </div>
              )}
              <div className="flex justify-between px-5 py-3.5 border-t-2 border-gray-200 bg-gray-50 font-bold"><span>Toplam Gelir</span><span className="tabular-nums text-green-600">{fmt(c.incomeTotal)}</span></div>
            </div>

            {/* gider */}
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100"><h2 className="text-base font-semibold text-red-600">Giderler</h2><span className="text-xs text-gray-500">Kullanıcı payı + Satış Ortaklığı otomatik + manuel</span></div>
              <div className="flex items-center gap-3 px-5 py-3 border-b border-gray-50 text-sm bg-gray-50">
                <span className="flex-1">Kullanıcı kazançları (cashback)</span>
                <span className="text-[10px] uppercase tracking-wide font-bold text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">Otomatik</span>
                <span className="tabular-nums font-medium">{fmt(c.cut)}</span>
              </div>

              {/* DEVİR — nakit esaslı: gider gerçekten ödendiği ayda yazılır */}
              {(report!.carryIn?.length ?? 0) > 0 && (
                <div className="px-5 py-3 border-b border-gray-50 bg-sky-50/60">
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="text-sm text-sky-800 font-medium flex-1">
                      ↪ Önceki dönemlerden devredip bu ay ödenenler
                    </span>
                    <span className="tabular-nums font-medium text-sky-800">
                      {fmt(report!.carryInTotal ?? 0)}
                    </span>
                  </div>
                  <div className="space-y-0.5 pl-4">
                    {report!.carryIn!.map((x: any, idx: number) => (
                      <div key={`ci-${idx}`} className="flex justify-between text-xs text-sky-700">
                        <span>{x.name} — {x.fromPeriodName} dönemi</span>
                        <span className="tabular-nums">{fmt(x.cut)}</span>
                      </div>
                    ))}
                  </div>
                  <p className="text-[11px] text-sky-600 mt-1.5 pl-4">
                    Bu tutarlar yukarıdaki "Kullanıcı kazançları" toplamına dahildir.
                  </p>
                </div>
              )}

              {(report!.carryOut?.length ?? 0) > 0 && (
                <div className="px-5 py-3 border-b border-gray-50 bg-amber-50/60">
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="text-sm text-amber-800 font-medium flex-1">
                      ↩ Bu aydan sonraki aya devredilenler (bu ay ödenmedi)
                    </span>
                    <span className="tabular-nums font-medium text-amber-800">
                      −{fmt(report!.carryOutTotal ?? 0)}
                    </span>
                  </div>
                  <div className="space-y-0.5 pl-4">
                    {report!.carryOut!.map((x: any, idx: number) => (
                      <div key={`co-${idx}`} className="flex justify-between text-xs text-amber-700">
                        <span>{x.name}</span>
                        <span className="tabular-nums">{fmt(x.cut)}</span>
                      </div>
                    ))}
                  </div>
                  <p className="text-[11px] text-amber-600 mt-1.5 pl-4">
                    Asgari ödeme tutarının altında kaldığı için bu ayın giderine yazılmadı.
                  </p>
                </div>
              )}
              <div className="flex items-center gap-3 px-5 py-3 border-b border-gray-50 text-sm bg-gray-50">
                <span className="flex-1">Satış Ortaklığı komisyonu <span className="text-xs text-gray-400">(getirilenlerin %20'si)</span></span>
                <span className="text-[10px] uppercase tracking-wide font-bold text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">Otomatik</span>
                <span className="tabular-nums font-medium">{fmt(c.affiliate)}</span>
              </div>
              {report!.expenseManual.map(i => (
                <div key={i.id} className="flex items-center gap-3 px-5 py-3 border-b border-gray-50 text-sm">
                  <span className="flex-1">{i.label}</span><span className="tabular-nums font-medium">{fmt(i.amount)}</span>
                  {!closed && <button onClick={() => delExpense(i.id)} className="text-gray-300 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>}
                </div>
              ))}
              {!closed && (
                <div className="flex items-center gap-2 px-4 py-3">
                  <input value={newExpense.label} onChange={e => setNewExpense(s => ({ ...s, label: e.target.value }))} placeholder="Gider açıklaması" className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-100 focus:border-blue-500" />
                  <input value={newExpense.amount} onChange={e => setNewExpense(s => ({ ...s, amount: e.target.value }))} onKeyDown={e => e.key === 'Enter' && addExpense()} placeholder="0,00" className="w-28 text-right tabular-nums px-3 py-2 border border-gray-200 rounded-lg text-sm" />
                  <button onClick={addExpense} className="inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-dashed border-gray-300 text-sm text-gray-500 hover:border-blue-500 hover:text-blue-600"><Plus className="w-4 h-4" /></button>
                </div>
              )}
              <div className="flex justify-between px-5 py-3.5 border-t-2 border-gray-200 bg-gray-50 font-bold"><span>Toplam Gider</span><span className="tabular-nums text-red-600">{fmt(c.expenseTotal)}</span></div>
            </div>
          </div>

          {/* özet */}
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100"><h2 className="text-base font-semibold text-gray-900">Özet — Kâr / Zarar</h2></div>
            <div className="py-2">
              <div className="flex items-center justify-between px-6 py-3 text-sm"><span className="text-gray-500">+ Toplam Gelir</span><span className="tabular-nums font-semibold text-green-600">{fmt(c.incomeTotal)}</span></div>
              <div className="flex items-center justify-between px-6 py-3 text-sm"><span className="text-gray-500">− Toplam Gider</span><span className="tabular-nums font-semibold text-red-600">{fmt(c.expenseTotal)}</span></div>
              <div className="flex items-center justify-between px-6 py-3 text-[15px] border-y border-gray-100"><span className="font-bold text-gray-800">= Net Kâr</span><span className="tabular-nums font-bold text-gray-800">{fmt(c.net)}</span></div>
              <div className="flex items-center justify-between px-6 py-3 text-sm">
                <span className="text-gray-500 flex items-center gap-2">− Paylaşım
                  <input type="number" min={0} max={100} value={workerRate} disabled={closed}
                    onChange={e => setWorkerRate(clampPct(e.target.value))}
                    onBlur={() => !closed && saveRates({ workerRatePercent: workerRate })}
                    className="w-14 text-right tabular-nums px-1.5 py-1 border border-gray-200 rounded-md text-sm disabled:bg-gray-50" />%</span>
                <span className="tabular-nums font-semibold text-amber-600">{fmt(c.worker)}</span>
              </div>
              <div className="flex items-center justify-between px-6 py-4 bg-amber-50"><span className="font-bold text-amber-700">= Nihai Net — Sana Kalan</span><span className="tabular-nums font-extrabold text-amber-600 text-xl">{fmt(c.final)}</span></div>
            </div>
          </div>

          {/* ÖDEME SEPETİ — seçilen hesapların payı + satış ortaklığı TEK toplamda */}
          {basket.lines.length > 0 && (
            <div className="sticky bottom-3 z-30">
              <div className="bg-gray-900 text-white rounded-xl shadow-2xl px-5 py-4">
                <div className="flex items-start gap-5 flex-wrap">
                  <div className="min-w-[190px]">
                    <div className="text-[11px] uppercase tracking-wide text-gray-400">Ödeme sepeti</div>
                    <div className="text-sm font-semibold">{basket.lines.length} hesap seçili</div>
                    <div className="mt-1 text-[11px] text-gray-400 leading-relaxed max-h-16 overflow-auto">
                      {basket.lines.map(l => l.name).join(' · ')}
                    </div>
                  </div>

                  <div className="flex items-center gap-6 flex-wrap ml-auto">
                    <div className="text-right">
                      <div className="text-[11px] uppercase tracking-wide text-gray-400">Hesap payları</div>
                      <div className="tabular-nums font-semibold">{fmt(basket.accountTotal)}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-[11px] uppercase tracking-wide text-purple-300">Satış ortaklığı</div>
                      <div className="tabular-nums font-semibold text-purple-200">{fmt(basket.affiliateTotal)}</div>
                    </div>
                    <div className="text-right border-l border-gray-700 pl-6">
                      <div className="text-[11px] uppercase tracking-wide text-amber-300">Genel toplam</div>
                      <div className="tabular-nums font-extrabold text-amber-300 text-xl">{fmt(basket.total)}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={copyBasket}
                        className="px-3 py-2 text-sm rounded-lg bg-white text-gray-900 font-semibold hover:bg-gray-100">
                        Kopyala
                      </button>
                      <button onClick={() => setSelected(new Set())}
                        className="px-3 py-2 text-sm rounded-lg border border-gray-600 text-gray-300 hover:bg-gray-800">
                        Temizle
                      </button>
                    </div>
                  </div>
                </div>

                {/* kırılım — hangi hesaptan ne geldiği görünsün */}
                <div className="mt-3 pt-3 border-t border-gray-800 max-h-32 overflow-auto">
                  <table className="w-full text-xs">
                    <tbody>
                      {basket.lines.map(l => (
                        <tr key={l.userId} className="text-gray-300">
                          <td className="py-1 pr-3">{l.name} <span className="text-gray-500">{l.email}</span></td>
                          <td className="py-1 text-right tabular-nums w-24">{l.cut > 0 ? fmt(l.cut) : '—'}</td>
                          <td className="py-1 text-right tabular-nums w-24 text-purple-200">{l.commission > 0 ? fmt(l.commission) : '—'}</td>
                          <td className="py-1 text-right tabular-nums w-24 font-semibold text-white">{fmt(r2(l.cut + l.commission))}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          <p className="text-xs text-gray-400 text-center">Tüm tutarlar USD. Muhasebe ayı = fiilen ödeme yapılan ay; o ay ödenen sipariş dönemi 3 ay öncesidir (ödeme penceresi). Kullanıcı payı = Ödeme Yönetimi'ndeki ödenebilir (doğrulanmış) tutar. Dönem kapatıldığında rakamlar dondurulur (snapshot). Oran yalnızca muhasebe içindir; kullanıcı ödemesini etkilemez.</p>
        </>
      )}
    </div>
  );
}
